import * as React from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import NavigationItem from './NavigationItem';

import { ErrorStateType, NavigationTabType } from '@models';

import { AuthenticationStatus } from '@store/types/auth';
import { app as routes } from '@configs/routes.json';

import { ReactComponent as ResultsIcon } from '@assets/images/results_default.svg';

import { ProjectPicker } from '@components';
import config from '@configs/config.json';
import { IApplicationState } from '@store';
import { getUploadStatus, UploadStatusDTO } from '@services';

import './Navigation.scss';

export interface NavigationStateProps {
  status: AuthenticationStatus;
  errors: ErrorStateType[];
  tabs: NavigationTabType[];
}

const BLOCKING_ERRORS = [
  ErrorStateType.MissingAccountAssociation,
  ErrorStateType.InvalidTOS,
];

const HIDDEN_ROUTES = [routes.almostThere];

const resultsListIconActive = (
  <ResultsIcon
    className='nav-item-icon nav-icon--active'
    title='results-icon'
  ></ResultsIcon>
);
const resultsListIcon = (
  <ResultsIcon
    className='nav-item-icon nav-icon--default'
    title='results-icon'
  ></ResultsIcon>
);

const Navigation: React.FC<NavigationStateProps> = ({
  status,
  errors,
  tabs,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [uploadState, setUploadState] =
    React.useState<UploadStatusDTO | undefined>(undefined);

  const { activeProjectId, uploadsChanges } = useSelector(
    (state: IApplicationState) => ({
      activeProjectId: state.projectPicker.activeProjectId,
      uploadsChanges: state.uploads.localChanges,
    })
  );

  React.useEffect(() => {
    // When activeProjectId changes, we don't want to show the
    // previous value to be visible until the new request returns.
    setUploadState(undefined);
  }, [activeProjectId]);

  React.useEffect(() => {
    const handleUploadStatus = () => {
      if (
        status === AuthenticationStatus.authenticated &&
        activeProjectId !== null
      ) {
        getUploadStatus().then((data) => setUploadState(data));
      }
    };

    handleUploadStatus();

    const id = setInterval(
      handleUploadStatus,
      config.upload.uploadPollingInterval
    );

    return () => clearInterval(id);
  }, [status, activeProjectId, uploadsChanges]);

  let uploadIcon: JSX.Element | string = '';
  if (uploadState?.anyFailed) {
    uploadIcon = (
      <span className='uploads-error fa-stack'>
        <i className='fas fa-circle fa-stack-1x'></i>
        <i className='fas fa-times-circle fa-stack-1x'></i>
      </span>
    );
  } else if (uploadState !== undefined && uploadState?.size > 0) {
    uploadIcon = <div className='uploads-count'>{uploadState.size}</div>;
  }

  return (
    <React.Fragment>
      {status === AuthenticationStatus.authenticated &&
      !HIDDEN_ROUTES.includes(location.pathname) &&
      _.intersection(errors, BLOCKING_ERRORS).length === 0 ? (
        <div className='navigation'>
          <div className='container d-flex'>
            {_.includes(tabs, NavigationTabType.ORDERS) ? (
              <NavigationItem route={routes.orders} iconClass='fas fa-list-ul'>
                <span>{t('common_orders')}</span>
              </NavigationItem>
            ) : null}
            {_.includes(tabs, NavigationTabType.RESULTS) ? (
              <NavigationItem route={routes.results}>
                {location.pathname === routes.results
                  ? resultsListIconActive
                  : resultsListIcon}
                {t('results_results')}
              </NavigationItem>
            ) : null}
            {_.includes(tabs, NavigationTabType.UPLOAD) ? (
              <NavigationItem
                route={routes.uploads}
                iconClass='fas fa-arrow-circle-up'
              >
                <span>{t('common_upload')}</span>
                {uploadIcon}
              </NavigationItem>
            ) : null}
            {_.includes(tabs, NavigationTabType.FILES) ? (
              <NavigationItem
                route={routes.files}
                iconClass='fas fa-file-download'
              >
                <span>{t('files_files')}</span>
              </NavigationItem>
            ) : null}
            {_.includes(tabs, NavigationTabType.HELP) ? (
              <NavigationItem
                route={routes.help}
                iconClass='fas fa-question-circle'
              >
                <span>{t('common_help')}</span>
              </NavigationItem>
            ) : null}
            <ProjectPicker />
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default Navigation;
